import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { getMainPageSlice } from '@store/features/filters/MainPageSlice/MainPageSlice';
import {
  fetchAnalyticsData,
  fetchDrillingActivitiesV2Data,
  fetchEquipmentData,
  fetchEquipmentConfigurations,
  fetchOverviewData,
  fetchOverviewKeyFiguresData,
  fetchOverviewKeyFiguresDataV2,
  fetchPowerMgmtData,
  fetchDefinedFocusAreas
} from '@api/Rig';
import { API_ROLLUP, BASELINE_SUB_SECTIONS, BASELINE_TYPE } from '@constants/index';
import { useTimezone } from '@hooks/useTimezone';
import { useCallback, useMemo } from 'react';
import { getLookupRigs } from '@store/features';
import { getBaselineComparisonSlice } from '@store/features/filters/BaselineComparisonSlice/BaselineComparisonSlice';

export const useBaselineQueries = () => {
  const queryClient = useQueryClient(); // Access queryClient instance
  const lookupRigs = useSelector(getLookupRigs);
  const { selectedBaseline, graphDisplays } = useSelector(getBaselineComparisonSlice);
  const { selectedRig, startDate: start, endDate: end } = useSelector(getMainPageSlice);
  const { calculateDates } = useTimezone();
  const { startDate: baseStart, endDate: baseEnd } = selectedBaseline || {};
  const { dateStart: mainStart, dateEnd: mainEnd } = calculateDates({startDate: start, endDate: end, useLocalTime: true});

  const visibleSection = useMemo(() => {
    let res = [];
    graphDisplays.forEach(({ graphName, visible }) => {
      if (visible) {
        res.push(graphName);
      }
    });
    return res;
  }, [graphDisplays]);

  const baselineRig = useMemo(() => {
    if (lookupRigs.length && selectedBaseline) {
      return lookupRigs.find(({ rigId }) => rigId === selectedBaseline.rigId) ?? null;
    }
    return null;
  }, [lookupRigs, selectedBaseline]);

  const isTargetBaseline = useMemo(() => {
    return selectedBaseline?.baselineTypeId === BASELINE_TYPE.TARGET;
  }, [selectedBaseline]);

  const baselineKeyFiguresQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'keyFig',
          'main',
          { selectedRig: selectedRig, startDate: mainStart, endDate: mainEnd }
        ],
        enabled: !!selectedRig && !!mainStart && !!mainEnd,
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchOverviewKeyFiguresDataV2(signal, queryKey[3], API_ROLLUP.HOUR)
            .then((result) => {
              if (!result?.calculations || !Array.isArray(result.calculations)) return null;
              return result.calculations.find(rig => rig.id == selectedRig.rigId) || null;
            })
            .catch(() => null);
        }
      },
      {
        queryKey: ['baseline', 'keyFig', 'base', { selectedBaseline: selectedBaseline }],
        enabled: !!selectedBaseline && !!baselineRig,
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (baselineRig) {
            if (selectedBaseline.baselineTypeId === BASELINE_TYPE.TARGET) return selectedBaseline;
            return fetchOverviewKeyFiguresData(
              signal,
              { selectedRig: baselineRig, startDate: baseStart, endDate: baseEnd },
              API_ROLLUP.HOUR
            )
              .then((result) => {
              if (!result?.calculations || !Array.isArray(result.calculations)) return null;
              return result.calculations.find(rig => rig.id == baselineRig.rigId) || null;
            })
              .catch(() => null);
          }
          return null;
        }
      }
    ]
  });
  const baselineOverviewQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'overview',
          'main',
          { selectedRig: selectedRig, startDate: mainStart, endDate: mainEnd }
        ],
        enabled:
          !!selectedRig &&
          !!mainStart &&
          !!mainEnd &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.PERIODIC_OVERVIEW),
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchOverviewData(signal, queryKey[3], API_ROLLUP.HOUR)
            .then((result) => result?.data)
            .catch(() => null);
        }
      },
      {
        queryKey: ['baseline', 'overview', 'base', { selectedBaseline: selectedBaseline }],
        enabled:
          !!selectedBaseline &&
          !!baselineRig &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.PERIODIC_OVERVIEW),
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (selectedBaseline.baselineTypeId === BASELINE_TYPE.TARGET) return selectedBaseline;
          if (baselineRig) {
            return fetchOverviewData(
              signal,
              { selectedRig: baselineRig, startDate: baseStart, endDate: baseEnd },
              API_ROLLUP.HOUR
            )
              .then((result) => result?.data)
              .catch(() => null);
          }
          return null;
        }
      }
    ]
  });
  const baselineDrillingQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'drilling',
          'main',
          { selectedRig: selectedRig, startDate: mainStart, endDate: mainEnd }
        ],
        enabled:
          !!selectedRig &&
          !!mainStart &&
          !!mainEnd &&
          !isTargetBaseline &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.DRILLING_ACTIVITIES),
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchDrillingActivitiesV2Data(signal, queryKey[3], API_ROLLUP.HOUR)
            .then((result) => result?.drillingActivities || [])
            .catch(() => []);
        }
      },
      {
        queryKey: ['baseline', 'drilling', 'base', { selectedBaseline: selectedBaseline }],
        enabled:
          !!selectedBaseline &&
          !!baselineRig &&
          !isTargetBaseline &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.DRILLING_ACTIVITIES),
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (baselineRig) {
            return fetchDrillingActivitiesV2Data(
              signal,
              { selectedRig: baselineRig, startDate: baseStart, endDate: baseEnd },
              API_ROLLUP.HOUR
            )
              .then((result) => result?.drillingActivities || [])
              .catch(() => []);
          }
          return [];
        }
      }
    ]
  });
  const baselineEquipmentListQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'equipmentList',
          'main',
          { selectedRig: selectedRig }
        ],
        enabled:
          !!selectedRig,
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchEquipmentConfigurations(signal, queryKey[3].selectedRig.rigId)
            .then((result) => result?.equipmentDetails || [])
            .catch(() => []);
        }
      },
    {
        queryKey: ['baseline', 'equipmentList', 'base', { selectedBaseline: selectedBaseline }],
        enabled:
          !!selectedBaseline &&
          !!baselineRig,
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (baselineRig) {
            return fetchEquipmentConfigurations(signal, baselineRig.rigId)
              .then((result) => result?.equipmentDetails || [])
              .catch(() => []);
          }
          return [];
        }
    }
  ]
});
  const baselineEquipmentQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'equipment',
          'main',
          { selectedRig: selectedRig, startDate: mainStart, endDate: mainEnd }
        ],
        enabled:
          !!selectedRig &&
          !!mainStart &&
          !!mainEnd &&
          !isTargetBaseline &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.EQUIPMENT),
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchEquipmentData(signal, queryKey[3], API_ROLLUP.HOUR)
            .then((result) => result?.componentCalculations || [])
            .catch(() => null);
        }
      },
      {
        queryKey: ['baseline', 'equipment', 'base', { selectedBaseline: selectedBaseline }],
        enabled:
          !!selectedBaseline &&
          !!baselineRig &&
          !isTargetBaseline &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.EQUIPMENT),
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (baselineRig) {
            return fetchEquipmentData(
              signal,
              { selectedRig: baselineRig, startDate: baseStart, endDate: baseEnd },
              API_ROLLUP.HOUR
            )
              .then((result) => result?.componentCalculations || [])
              .catch(() => null);
          }
          return null;
        }
      }
    ]
  });
  const baselinePowerMgmtQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'powerMgmt',
          'main',
          { selectedRig: selectedRig, startDate: mainStart, endDate: mainEnd }
        ],
        enabled:
          !!selectedRig &&
          !!mainStart &&
          !!mainEnd &&
          !isTargetBaseline &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.POWER_MGMT),
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchPowerMgmtData(signal, queryKey[3], API_ROLLUP.HOUR)
            .then((result) => result)
            .catch(() => null);
        }
      },
      {
        queryKey: ['baseline', 'powerMgmt', 'base', { selectedBaseline: selectedBaseline }],
        enabled:
          !!selectedBaseline &&
          !!baselineRig &&
          !isTargetBaseline &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.POWER_MGMT),
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (baselineRig) {
            return fetchPowerMgmtData(
              signal,
              { selectedRig: baselineRig, startDate: baseStart, endDate: baseEnd },
              API_ROLLUP.HOUR
            )
              .then((result) => result)
              .catch(() => null);
          }
          return null;
        }
      }
    ]
  });
  const baselineAnalyticsQueries = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'analytics',
          'main',
          { selectedRig: selectedRig, startDate: mainStart, endDate: mainEnd }
        ],
        enabled:
          !!selectedRig &&
          !!mainStart &&
          !!mainEnd &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.ANALYTICS),
        placeholderData: null,
        queryFn: ({ queryKey, signal }) => {
          return fetchAnalyticsData(signal, queryKey[3], API_ROLLUP.MIN)
            .then((result) => {
              const rigData = result?.data?.find(item => item.rigId === selectedRig.rigId);
              return rigData?.analytics ?? null;
            })
            .catch(() => null);
        }
      },
      {
        queryKey: ['baseline', 'analytics', 'base', { selectedBaseline: selectedBaseline }],
        enabled:
          !!selectedBaseline &&
          !!baselineRig &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.ANALYTICS),
        placeholderData: null,
        queryFn: ({ signal }) => {
          if (selectedBaseline.baselineTypeId === BASELINE_TYPE.TARGET) return selectedBaseline;
          if (baselineRig) {
            return fetchAnalyticsData(
              signal,
              { selectedRig: baselineRig, startDate: baseStart, endDate: baseEnd },
              API_ROLLUP.MIN
            )
              .then((result) => {
                const rigData = result?.data?.find(item => item.rigId === baselineRig.rigId);
                return rigData?.analytics ?? null;
              })
              .catch(() => null);
          }
          return null;
        }
      }
    ]
  });

  const focusAreas = useQueries({
    queries: [
      {
        queryKey: [
          'baseline',
          'focusarea',
          'main',
          { selectedRig: selectedRig }
        ],
        enabled:
          !!selectedRig &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.ANALYTICS),
        placeholderData: null,
        queryFn: async ({signal}) => {
          const definedFocusAreas = await getDefinedRigFocusAreas(signal, selectedRig)
              .then((result) => result)
              .catch(() => null);;
          return definedFocusAreas;
        }
      },
      {
        queryKey: [
          'baseline',
          'focusarea',
          'base',
          { baselineRig: baselineRig }
        ],
        enabled:
          !!baselineRig &&
          !!visibleSection.includes(BASELINE_SUB_SECTIONS.ANALYTICS),
        placeholderData: null,
        queryFn: async ({signal}) => {
          const definedFocusAreas = await getDefinedRigFocusAreas(signal, baselineRig)
              .then((result) => result)
              .catch(() => null);;
          return definedFocusAreas;
        }
      }
    ]
  });  
  
  async function getDefinedRigFocusAreas(signal, rig) {
    const result = await fetchDefinedFocusAreas(signal ?? {}, rig);
    let focusAreas = (result ?? []).map(fa => { return fa.name; });
    return focusAreas;
  };

  const resetBaselinePageData = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['baseline', 'overview'] });
    await queryClient.invalidateQueries({ queryKey: ['baseline', 'keyFig'] });
    await queryClient.invalidateQueries({ queryKey: ['baseline', 'drilling'] });
    await queryClient.invalidateQueries({ queryKey: ['baseline', 'equipment'] });
    await queryClient.invalidateQueries({ queryKey: ['baseline', 'powerMgmt'] });
    await queryClient.invalidateQueries({ queryKey: ['baseline', 'analytics'] });
  }, [queryClient]);

  return {
    baselineOverviewQueries,
    baselineDrillingQueries,
    baselineEquipmentQueries,
    baselineEquipmentListQueries,
    baselinePowerMgmtQueries,
    baselineAnalyticsQueries,
    baselineKeyFiguresQueries,
    resetBaselinePageData,
    focusAreas
  };
};
